import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCheck,
  Download,
  ExternalLink,
  FileText,
  History,
  Lock,
  Paperclip,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Separator } from '@/components/ui/Separator';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import { formatCurrency, formatDate, formatRelative } from '@/lib/formatters';
import {
  getQboHistoricalReceiptUrl,
  useQboHistoricalTransaction,
} from '@/features/financials/hooks';
import type { Tables } from '@/types/database';

type AttachmentRow = Tables<'qbo_historical_attachments'>;

/**
 * Read-only detail surface for a single QBO history row (and its sibling
 * posting lines). Reached by clicking a row on the History tab.
 *
 * Layout reasoning:
 *   - Header: date + amount of the clicked posting line, plus a
 *     read-only "sourced from QuickBooks" lockup so the surface is
 *     visually distinct from the live TransactionDetailPage.
 *   - "Transaction" card: the per-transaction fields (type, doc #,
 *     payee, memo, cleared, posting type) — values that QBO holds at
 *     the parent transaction, not the line.
 *   - "Posting lines" card: every qbo_historical_transactions row that
 *     shares qbo_txn_id with the clicked one. Single-line transactions
 *     show one row; splits show one row per side. Sum is displayed at
 *     the bottom so the operator can sanity-check the split balances.
 *   - "Receipts" card: every Attachable linked to this transaction, with
 *     paperclip + preview-on-click. Identical UX to the Receipt column
 *     on the table.
 *   - "System" card: synced_at + the raw QBO Id, so the operator can
 *     deep-link into QBO from here if needed.
 */
export function QboHistoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useQboHistoricalTransaction(id);

  const [previewAttachment, setPreviewAttachment] = useState<AttachmentRow | null>(null);

  if (error) {
    return (
      <div className="space-y-6">
        <BackLink />
        <PageHeader
          title="History transaction"
        />
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <BackLink />
        <PageHeader title="Loading historical transaction…" />
        <Card>
          <CardContent className="space-y-3 pt-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  const { row, siblings, attachments } = data;
  const splitSum = siblings.reduce((s, r) => s + r.amount_cents, 0);
  const isMultiLine = siblings.length > 1;

  return (
    <div className="space-y-6">
      <BackLink />

      <PageHeader
        eyebrow="Financials / Transactions · History"
        title={`${row.qbo_txn_type} · ${formatDate(row.posted_at)}`}
        meta={row.payee_name ?? 'No payee recorded in QuickBooks.'}
      />

      {/* Read-only banner — same visual language as the History tab so the
          operator stays oriented to "this is historical, immutable data". */}
      <div className="flex items-start gap-3 rounded-lg border border-border bg-secondary/40 px-4 py-3 text-sm">
        <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="space-y-0.5">
          <p className="font-medium text-foreground">Read-only · sourced from QuickBooks</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Transaction-level fields — values held at the parent transaction
            in QBO, identical across every posting line of a split. */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Transaction</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <KV label="Date" value={formatDate(row.posted_at)} />
            <KV
              label="Type"
              value={
                <Badge variant="outline" className="font-normal">
                  {row.qbo_txn_type}
                </Badge>
              }
            />
            <KV
              label="Doc #"
              value={
                row.doc_number ? (
                  <span className="font-mono text-xs">{row.doc_number}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )
              }
            />
            <KV
              label="Payee"
              value={row.payee_name ?? <span className="text-muted-foreground">—</span>}
            />
            <KV
              label="Memo"
              value={
                row.memo ? (
                  <span className="whitespace-pre-wrap text-foreground">{row.memo}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )
              }
            />
            <KV label="Cleared" value={<ClearedPill cleared={row.is_cleared} />} />
            <KV
              label="Posting type"
              value={
                row.posting_type ? (
                  <span>{row.posting_type}</span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )
              }
            />
          </CardContent>
        </Card>

        {/* Amount card — the clicked line's amount, prominently displayed,
            with the totals strip below if this is a multi-line split. */}
        <Card>
          <CardHeader>
            <CardTitle>Amount</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p
              className={
                'text-3xl font-semibold tabular-nums ' +
                (row.amount_cents >= 0 ? 'text-bright-moss-dark' : 'text-foreground')
              }
            >
              {row.amount_cents >= 0 ? '+' : '−'}
              {formatCurrency(Math.abs(row.amount_cents))}
            </p>
            <KV label="Account" value={row.account_name} />
            <KV
              label="Category / split"
              value={
                row.split_account_name ?? (
                  <span className="text-muted-foreground">—</span>
                )
              }
            />
            {isMultiLine && (
              <>
                <Separator />
                <KV
                  label={`Split total (${siblings.length} lines)`}
                  value={
                    <span
                      className={
                        'tabular-nums ' +
                        (splitSum === 0
                          ? 'text-muted-foreground'
                          : splitSum > 0
                            ? 'text-bright-moss-dark'
                            : 'text-foreground')
                      }
                    >
                      {splitSum >= 0 ? '+' : '−'}
                      {formatCurrency(Math.abs(splitSum))}
                    </span>
                  }
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Posting lines — only render the card if there's more than one,
          since for a single line it just repeats the Amount card. */}
      {isMultiLine && (
        <Card>
          <CardHeader>
            <CardTitle>Posting lines</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="min-w-full text-sm">
                <thead className="bg-secondary/40 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Account</th>
                    <th className="px-3 py-2 text-left font-medium">Category / split</th>
                    <th className="px-3 py-2 text-left font-medium">Posting</th>
                    <th className="px-3 py-2 text-right font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {siblings.map((s) => (
                    <tr
                      key={s.id}
                      className={
                        'border-t border-border ' +
                        (s.id === row.id ? 'bg-secondary/30' : '')
                      }
                    >
                      <td className="px-3 py-2">{s.account_name}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {s.split_account_name ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {s.posting_type ?? '—'}
                      </td>
                      <td
                        className={
                          'px-3 py-2 text-right tabular-nums ' +
                          (s.amount_cents >= 0
                            ? 'text-bright-moss-dark'
                            : 'text-foreground')
                        }
                      >
                        {s.amount_cents >= 0 ? '+' : '−'}
                        {formatCurrency(Math.abs(s.amount_cents))}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-secondary/20">
                    <td colSpan={3} className="px-3 py-2 text-xs text-muted-foreground">
                      Total
                    </td>
                    <td
                      className={
                        'px-3 py-2 text-right tabular-nums ' +
                        (splitSum === 0
                          ? 'text-muted-foreground'
                          : splitSum > 0
                            ? 'text-bright-moss-dark'
                            : 'text-foreground')
                      }
                    >
                      {splitSum >= 0 ? '+' : '−'}
                      {formatCurrency(Math.abs(splitSum))}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-muted-foreground" />
            Receipts
            {attachments.length > 0 && (
              <Badge variant="outline">{attachments.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {attachments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No receipts on file.</p>
          ) : (
            <ul className="space-y-2">
              {attachments.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground">
                        {a.file_name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {a.content_type ?? 'unknown type'}
                        {a.size_bytes ? ` · ${formatBytes(a.size_bytes)}` : ''}
                        {a.category ? ` · ${a.category}` : ''}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPreviewAttachment(a)}
                  >
                    <Paperclip className="h-3.5 w-3.5" />
                    Open
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-4 w-4 text-muted-foreground" />
            System
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <KV
            label="QuickBooks Id"
            value={
              row.qbo_txn_id ? (
                <span className="font-mono text-xs">{row.qbo_txn_id}</span>
              ) : (
                <span className="text-muted-foreground">
                  Not captured — pull from QuickBooks again
                </span>
              )
            }
          />
          <KV
            label="Atlas row Id"
            value={<span className="font-mono text-xs">{row.id}</span>}
          />
          <KV
            label="Last synced"
            value={
              <span title={row.synced_at}>{formatRelative(row.synced_at)}</span>
            }
          />
        </CardContent>
      </Card>

      <ReceiptPreviewSheet
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Small, page-local primitives. Same shape as TransactionDetailPage's BackLink
// + KV but pointed at the History tab instead of the live transactions list.
// -----------------------------------------------------------------------------
function BackLink() {
  return (
    <Link
      to="/financials/transactions?tab=history"
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      Back to history
    </Link>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right text-foreground">{value}</span>
    </div>
  );
}

function ClearedPill({ cleared }: { cleared: string | null }) {
  if (cleared === 'R') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-bright-moss-dark">
        <CheckCheck className="h-3 w-3" />
        Reconciled
      </span>
    );
  }
  if (cleared === 'C') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-atmosphere-dark">
        <CheckCheck className="h-3 w-3" />
        Cleared
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">—</span>;
}

// -----------------------------------------------------------------------------
// Receipt preview Sheet — identical to the one inlined into QboHistoryTab.
// Lives here too so the detail page can preview without bouncing back to
// the table. Could be lifted into its own module if a third consumer
// appears.
// -----------------------------------------------------------------------------
function ReceiptPreviewSheet({
  attachment,
  onClose,
}: {
  attachment: AttachmentRow | null;
  onClose: () => void;
}) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [urlError, setUrlError] = useState<string | null>(null);

  useEffect(() => {
    setSignedUrl(null);
    setUrlError(null);
    if (!attachment) return;
    let cancelled = false;
    (async () => {
      const url = await getQboHistoricalReceiptUrl(attachment.storage_path);
      if (cancelled) return;
      if (!url) {
        setUrlError(
          'Could not generate a signed URL for this receipt. Make sure the file exists in the qbo-historical-receipts bucket.',
        );
        return;
      }
      setSignedUrl(url);
    })();
    return () => {
      cancelled = true;
    };
  }, [attachment]);

  const isImage = (attachment?.content_type ?? '').startsWith('image/');
  const isPdf = (attachment?.content_type ?? '') === 'application/pdf';

  return (
    <Sheet open={!!attachment} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" />
            {attachment?.file_name ?? 'Receipt'}
          </SheetTitle>
          <SheetDescription className="space-x-2 text-xs">
            <span>{attachment?.content_type ?? 'unknown type'}</span>
            {attachment?.size_bytes ? (
              <span>· {formatBytes(attachment.size_bytes)}</span>
            ) : null}
            {attachment?.category ? <span>· {attachment.category}</span> : null}
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-3">
          {attachment?.note && (
            <p className="rounded-md border border-border bg-secondary/30 px-3 py-2 text-xs text-muted-foreground">
              {attachment.note}
            </p>
          )}
          {urlError && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {urlError}
            </p>
          )}
          {!signedUrl && !urlError && (
            <p className="text-xs text-muted-foreground">Loading…</p>
          )}
          {signedUrl && (
            <>
              {isImage && (
                <img
                  src={signedUrl}
                  alt={attachment?.file_name ?? 'Receipt'}
                  className="max-h-[70vh] w-full rounded-md border border-border object-contain"
                />
              )}
              {isPdf && (
                <iframe
                  src={signedUrl}
                  title={attachment?.file_name ?? 'Receipt'}
                  className="h-[70vh] w-full rounded-md border border-border"
                />
              )}
              {!isImage && !isPdf && (
                <p className="text-xs text-muted-foreground">
                  Preview unavailable for this file type.
                </p>
              )}
              <div className="flex gap-2 pt-2">
                <Button size="sm" variant="outline" asChild>
                  <a href={signedUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open in new tab
                  </a>
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <a
                    href={signedUrl}
                    download={attachment?.file_name ?? 'receipt'}
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </a>
                </Button>
              </div>
            </>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb < 10 ? 1 : 0)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
}
